import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, MapPin, Heart, Shield, Ban, Settings, Users, Award, Wrench, ClipboardCheck } from 'lucide-react';
import { companyInfo, aboutText, values, strengths, strengthsIntro, strengthsQuote, projectSteps, projectStepsMessage } from '../data/mockData';

const AboutPage = () => {
  // Icons for values
  const valueIcons = {
    "Proximité": MapPin,
    "Engagement": Heart,
    "Confiance": Shield
  };

  // Icons for strengths
  const strengthIcons = [
    Ban, // Zéro sous-traitance
    Settings, // Solutions personnalisées
    Users, // Un interlocuteur unique
    Award, // Les meilleures garanties
    ClipboardCheck, // Projet clé en main
    Wrench // Bureau d'études interne
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section - Compact */}
      <section className="bg-gradient-to-br from-[#03224c] to-[#03224c]/90 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              À propos de Sunpac Energy
            </h1>
            <p className="text-lg text-gray-200">
              Votre partenaire de confiance pour la transition énergétique en Suisse romande
            </p>
          </div>
        </div>
      </section>

      {/* Qui sommes-nous - Compact */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
            <div>
              <img
                src="/images/lausanne-lake.jpeg"
                alt="Lausanne - Suisse romande"
                className="rounded-xl shadow-xl w-full h-96 object-cover"
              />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#03224c] mb-4">Qui sommes-nous ?</h2>
              <p className="text-gray-700 mb-3 leading-relaxed">
                {aboutText.intro}
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {aboutText.vision}
              </p>
              <div className="bg-[#e57200]/10 p-4 rounded-lg border-l-4 border-[#e57200]">
                <p className="text-sm text-gray-800 italic">
                  "{aboutText.quote}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values - Compact with icons */}
      <section className="py-10 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-[#03224c]">
              Nos valeurs
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              return (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="w-16 h-16 bg-[#e57200] rounded-full flex items-center justify-center mx-auto mb-4">
                    {IconComponent && <IconComponent className="text-white" size={32} />}
                  </div>
                  <h3 className="text-xl font-bold text-[#03224c] mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nos forces - Compact with icons */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-[#03224c] mb-3">
                Nos forces
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {strengthsIntro}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {strengths.map((strength, index) => {
                const IconComponent = strengthIcons[index];
                return (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-5 rounded-xl shadow-md border-l-4 border-[#e57200]">
                    <div className="flex items-start space-x-3 mb-2">
                      <div className="w-10 h-10 bg-[#e57200] rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-white" size={20} />
                      </div>
                      <h3 className="font-bold text-[#03224c] text-base">{strength.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed pl-13">{strength.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 bg-[#e57200] text-white p-5 rounded-xl text-center max-w-3xl mx-auto">
              <p className="font-bold">
                {strengthsQuote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre processus - Compact */}
      <section className="py-10 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-[#03224c] mb-3">
                Notre processus
              </h2>
              <p className="text-gray-600">
                {projectStepsMessage}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {projectSteps.map((step, index) => (
                <div key={index} className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-[#e57200] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{step.step}</span>
                    </div>
                    <h3 className="font-bold text-[#03224c] text-base">{step.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-12 bg-gradient-to-r from-[#03224c] to-[#03224c]/90">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-base mb-6 text-gray-200">
              Contactez-nous pour une étude personnalisée et gratuite. Notre équipe vous accompagne de A à Z pour votre projet de rénovation énergétique.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <button className="px-8 py-3 bg-[#e57200] text-white rounded-lg font-bold hover:bg-[#e57200]/90 transition-all shadow-lg">
                  DEVIS GRATUIT
                </button>
              </Link>
              <a href="tel:021391789">
                <button className="px-8 py-3 border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-all">
                  021 391 78 99
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
